/**
 * Leviathan - Delivery Program Detail Page
 * Quasar Prime LLC
 *
 * View and manage individual delivery program
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2,
  ArrowLeft,
  Edit,
  Gauge,
  Phone,
  Calendar,
  Fuel,
  MapPin,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface DeliveryHistory {
  id: string;
  deliveryDate: string;
  gallonsDelivered: number;
  preTankLevel: number | null;
  postTankLevel: number | null;
  status: string;
  driverName: string | null;
  truckNumber: string | null;
  bolNumber: string | null;
  notes: string | null;
}

interface DeliveryProgram {
  id: string;
  customerId: string;
  customerName: string;
  locationId: string;
  locationName: string;
  locationAddress: string;
  programType: 'KEEP_FULL' | 'WILL_CALL' | 'SCHEDULED';
  productId: string;
  productName: string;
  tankCapacityGallons: number;
  currentTankLevel: number | null;
  triggerPercentage: number | null;
  targetPercentage: number | null;
  scheduleFrequency: string | null;
  scheduleDayOfWeek: number | null;
  scheduleDayOfMonth: number | null;
  customScheduleDays: number | null;
  lastDeliveryDate: string | null;
  nextScheduledDate: string | null;
  estimatedGallonsNeeded: number | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
}

interface ProgramStats {
  totalDeliveries: number;
  totalGallonsDelivered: number;
  averageGallonsPerDelivery: number;
  averageDaysBetweenDeliveries: number;
  lastDeliveryDate: string | null;
  lastDeliveryGallons: number | null;
}

const PROGRAM_TYPE_CONFIG = {
  KEEP_FULL: {
    label: 'Keep Full',
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    icon: Gauge,
    description: 'Auto-deliver when tank drops below trigger level',
  },
  WILL_CALL: {
    label: 'Will Call',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    icon: Phone,
    description: 'Customer calls to request delivery',
  },
  SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    icon: Calendar,
    description: 'Fixed recurring delivery schedule',
  },
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Every 2 Weeks',
  MONTHLY: 'Monthly',
  CUSTOM: 'Custom Interval',
};

export default function DeliveryProgramDetail() {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showRecordModal, setShowRecordModal] = useState(false);

  // Fetch program details
  const { data: programData, isLoading: programLoading } = useQuery({
    queryKey: ['delivery-program', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch program');
      return res.json();
    },
  });

  // Fetch program stats
  const { data: statsData } = useQuery({
    queryKey: ['delivery-program-stats', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/${id}/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  // Fetch delivery history
  const { data: historyData } = useQuery({
    queryKey: ['delivery-program-history', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/${id}/deliveries`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch history');
      return res.json();
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await fetch(`${API_URL}/api/delivery-programs/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-program', id] });
    },
  });

  const program: DeliveryProgram | null = programData?.program || null;
  const stats: ProgramStats | null = statsData?.stats || null;
  const deliveries: DeliveryHistory[] = historyData?.deliveries || [];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (programLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="text-center py-12">
        <Gauge className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">Program not found</p>
        <Link to="/operations/delivery-programs" className="text-amber-400 hover:text-amber-300">
          Back to Delivery Programs
        </Link>
      </div>
    );
  }

  const typeConfig = PROGRAM_TYPE_CONFIG[program.programType];
  const TypeIcon = typeConfig.icon;

  const needsDelivery =
    program.programType === 'KEEP_FULL' &&
    program.currentTankLevel !== null &&
    program.triggerPercentage !== null &&
    program.currentTankLevel <= program.triggerPercentage;

  const estimatedGallonsNeeded =
    program.currentTankLevel !== null && program.targetPercentage !== null
      ? Math.round(
          ((program.targetPercentage - program.currentTankLevel) / 100) *
            program.tankCapacityGallons
        )
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to="/operations/delivery-programs"
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">{program.customerName}</h1>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded ${typeConfig.color}`}
            >
              <TypeIcon className="w-3 h-3" />
              {typeConfig.label}
            </span>
            {!program.isActive && (
              <span className="px-2.5 py-1 text-xs bg-slate-500/20 text-slate-400 border border-slate-500/50 rounded">
                INACTIVE
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm ml-9">
            {program.productName} - {program.locationName}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={`/operations/delivery-programs/${id}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={() => toggleActiveMutation.mutate(!program.isActive)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              program.isActive
                ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
            }`}
          >
            {program.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => setShowRecordModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Record Delivery
          </button>
        </div>
      </div>

      {/* Needs Delivery Alert */}
      {needsDelivery && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div>
              <div className="text-red-400 font-semibold">Tank Below Trigger Level</div>
              <div className="text-slate-400 text-sm">
                Current: {program.currentTankLevel}% | Trigger: {program.triggerPercentage}% |
                Est. needed: {estimatedGallonsNeeded?.toLocaleString()} gallons
              </div>
            </div>
          </div>
          <button className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors">
            Create Order
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {program.currentTankLevel !== null && (
          <StatCard
            label="Tank Level"
            value={`${program.currentTankLevel}%`}
            subtext={`${Math.round(
              (program.currentTankLevel / 100) * program.tankCapacityGallons
            ).toLocaleString()} / ${program.tankCapacityGallons.toLocaleString()} gal`}
            status={needsDelivery ? 'warning' : undefined}
          />
        )}
        <StatCard label="Last Delivery" value={formatDate(program.lastDeliveryDate)} />
        {program.programType === 'SCHEDULED' && (
          <StatCard label="Next Scheduled" value={formatDate(program.nextScheduledDate)} />
        )}
        {stats && (
          <>
            <StatCard
              label="Total Delivered"
              value={`${stats.totalGallonsDelivered.toLocaleString()} gal`}
              subtext={`${stats.totalDeliveries} deliveries`}
            />
            <StatCard
              label="Avg Per Delivery"
              value={`${Math.round(stats.averageGallonsPerDelivery).toLocaleString()} gal`}
              subtext={
                stats.averageDaysBetweenDeliveries > 0
                  ? `Every ${Math.round(stats.averageDaysBetweenDeliveries)} days`
                  : undefined
              }
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Program Settings */}
          <Panel title="Program Settings">
            {program.programType === 'KEEP_FULL' && (
              <div className="grid grid-cols-3 gap-6">
                <InfoRow label="Trigger Level" value={`${program.triggerPercentage}%`} />
                <InfoRow label="Target Level" value={`${program.targetPercentage}%`} />
                <InfoRow
                  label="Tank Capacity"
                  value={`${program.tankCapacityGallons.toLocaleString()} gal`}
                />
              </div>
            )}

            {program.programType === 'SCHEDULED' && (
              <div className="grid grid-cols-3 gap-6">
                <InfoRow
                  label="Frequency"
                  value={FREQUENCY_LABELS[program.scheduleFrequency || ''] || '-'}
                />
                {program.scheduleDayOfWeek !== null && (
                  <InfoRow
                    label="Day of Week"
                    value={DAY_NAMES[program.scheduleDayOfWeek] || '-'}
                  />
                )}
                {program.scheduleDayOfMonth !== null && (
                  <InfoRow label="Day of Month" value={program.scheduleDayOfMonth.toString()} />
                )}
                {program.customScheduleDays && (
                  <InfoRow label="Interval" value={`Every ${program.customScheduleDays} days`} />
                )}
                <InfoRow label="Next Delivery" value={formatDate(program.nextScheduledDate)} />
              </div>
            )}

            {program.programType === 'WILL_CALL' && (
              <div className="text-slate-400">
                Customer calls to request delivery as needed. No automatic scheduling.
              </div>
            )}
          </Panel>

          {/* Notes */}
          {program.notes && (
            <Panel title="Notes">
              <div className="text-slate-300 whitespace-pre-wrap">{program.notes}</div>
            </Panel>
          )}

          {/* Delivery History */}
          <Panel title="Delivery History">
            {deliveries.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No deliveries recorded yet</div>
            ) : (
              <div className="space-y-3">
                {deliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-slate-100 font-semibold">
                          {delivery.gallonsDelivered.toLocaleString()} gallons
                        </div>
                        <div className="text-slate-500 text-xs">
                          {formatDateTime(delivery.deliveryDate)}
                        </div>
                      </div>
                      <div className="text-right">
                        {delivery.preTankLevel !== null && delivery.postTankLevel !== null && (
                          <div className="text-slate-400 text-sm">
                            {delivery.preTankLevel}% → {delivery.postTankLevel}%
                          </div>
                        )}
                        <span
                          className={`px-2 py-0.5 text-xs rounded ${
                            delivery.status === 'DELIVERED'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-slate-500/20 text-slate-400'
                          }`}
                        >
                          {delivery.status}
                        </span>
                      </div>
                    </div>
                    {delivery.driverName && (
                      <div className="mt-2 text-slate-500 text-xs">
                        Driver: {delivery.driverName}
                        {delivery.truckNumber && ` | Truck: ${delivery.truckNumber}`}
                      </div>
                    )}
                    {delivery.notes && (
                      <div className="mt-2 text-slate-500 text-xs">{delivery.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Panel title="Customer">
            <div className="space-y-3">
              <Link
                to={`/customers/${program.customerId}`}
                className="text-amber-400 hover:text-amber-300 font-semibold block"
              >
                {program.customerName}
              </Link>
              <div className="flex items-start gap-2 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  {program.locationName}
                  {program.locationAddress && (
                    <>
                      <br />
                      {program.locationAddress}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Panel>

          {/* Tank Visualization */}
          {program.programType === 'KEEP_FULL' && program.currentTankLevel !== null && (
            <Panel title="Tank Level">
              <div className="space-y-4">
                <div className="relative h-32 bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
                  {/* Trigger line */}
                  {program.triggerPercentage !== null && (
                    <div
                      className="absolute left-0 right-0 border-t-2 border-red-500 border-dashed z-10"
                      style={{ bottom: `${program.triggerPercentage}%` }}
                    >
                      <span className="absolute -top-4 right-1 text-[10px] text-red-400">
                        Trigger {program.triggerPercentage}%
                      </span>
                    </div>
                  )}
                  {/* Target line */}
                  {program.targetPercentage !== null && (
                    <div
                      className="absolute left-0 right-0 border-t-2 border-emerald-500 border-dashed z-10"
                      style={{ bottom: `${program.targetPercentage}%` }}
                    >
                      <span className="absolute -top-4 right-1 text-[10px] text-emerald-400">
                        Target {program.targetPercentage}%
                      </span>
                    </div>
                  )}
                  {/* Fill */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 transition-all ${
                      needsDelivery
                        ? 'bg-red-500/60'
                        : program.currentTankLevel < 50
                        ? 'bg-yellow-500/60'
                        : 'bg-emerald-500/60'
                    }`}
                    style={{ height: `${program.currentTankLevel}%` }}
                  />
                  {/* Level text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white drop-shadow-lg">
                      {program.currentTankLevel}%
                    </span>
                  </div>
                </div>
                <div className="text-center text-slate-400 text-sm">
                  {Math.round(
                    (program.currentTankLevel / 100) * program.tankCapacityGallons
                  ).toLocaleString()}{' '}
                  / {program.tankCapacityGallons.toLocaleString()} gallons
                </div>
              </div>
            </Panel>
          )}

          {/* Product */}
          <Panel title="Product">
            <div className="flex items-center gap-2">
              <Fuel className="w-4 h-4 text-amber-400" />
              <span className="text-slate-100">{program.productName}</span>
            </div>
          </Panel>

          {/* Metadata */}
          <Panel title="Details">
            <div className="space-y-3">
              <InfoRow label="Created" value={formatDate(program.createdAt)} />
              <InfoRow label="Program ID" value={program.id.slice(0, 8)} />
            </div>
          </Panel>
        </div>
      </div>

      {/* Record Delivery Modal */}
      {showRecordModal && accessToken && (
        <RecordDeliveryModal
          programId={id!}
          tankCapacity={program.tankCapacityGallons}
          currentLevel={program.currentTankLevel}
          accessToken={accessToken}
          onClose={() => setShowRecordModal(false)}
          onSuccess={() => {
            setShowRecordModal(false);
            queryClient.invalidateQueries({ queryKey: ['delivery-program', id] });
            queryClient.invalidateQueries({ queryKey: ['delivery-program-stats', id] });
            queryClient.invalidateQueries({ queryKey: ['delivery-program-history', id] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  status,
}: {
  label: string;
  value: string;
  subtext?: string;
  status?: 'warning' | 'error';
}) {
  return (
    <div
      className={`bg-slate-800/50 border rounded-xl p-4 ${
        status === 'error'
          ? 'border-red-500/50'
          : status === 'warning'
          ? 'border-yellow-500/50'
          : 'border-slate-700/50'
      }`}
    >
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div
        className={`text-xl font-bold ${
          status === 'warning'
            ? 'text-yellow-400'
            : status === 'error'
            ? 'text-red-400'
            : 'text-slate-100'
        }`}
      >
        {value}
      </div>
      {subtext && <div className="text-slate-500 text-xs mt-1">{subtext}</div>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-slate-100">{value}</div>
    </div>
  );
}

function RecordDeliveryModal({
  programId,
  tankCapacity: _tankCapacity,
  currentLevel,
  accessToken,
  onClose,
  onSuccess,
}: {
  programId: string;
  tankCapacity: number;
  currentLevel: number | null;
  accessToken: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    deliveryDate: new Date().toISOString().split('T')[0],
    gallonsDelivered: '',
    preTankLevel: currentLevel?.toString() || '',
    postTankLevel: '',
    driverName: '',
    truckNumber: '',
    bolNumber: '',
    notes: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/delivery-programs/${programId}/deliveries`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            deliveryDate: formData.deliveryDate,
            gallonsDelivered: parseFloat(formData.gallonsDelivered),
            preTankLevel: formData.preTankLevel ? parseFloat(formData.preTankLevel) : null,
            postTankLevel: formData.postTankLevel ? parseFloat(formData.postTankLevel) : null,
            driverName: formData.driverName || null,
            truckNumber: formData.truckNumber || null,
            bolNumber: formData.bolNumber || null,
            notes: formData.notes || null,
          }),
        }
      );

      if (response.ok) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to record delivery:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-100">Record Delivery</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-400 text-sm mb-2">Delivery Date *</label>
            <input
              type="date"
              value={formData.deliveryDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, deliveryDate: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Gallons Delivered *</label>
            <input
              type="number"
              value={formData.gallonsDelivered}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, gallonsDelivered: e.target.value }))
              }
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              placeholder="0"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Tank % Before</label>
              <input
                type="number"
                value={formData.preTankLevel}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, preTankLevel: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Tank % After</label>
              <input
                type="number"
                value={formData.postTankLevel}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, postTankLevel: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                min="0"
                max="100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Driver Name</label>
              <input
                type="text"
                value={formData.driverName}
                onChange={(e) => setFormData((prev) => ({ ...prev, driverName: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Truck #</label>
              <input
                type="text"
                value={formData.truckNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, truckNumber: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">BOL Number</label>
            <input
              type="text"
              value={formData.bolNumber}
              onChange={(e) => setFormData((prev) => ({ ...prev, bolNumber: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Record Delivery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
